import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useT } from '../../../i18n';
import type { ApiError } from '../../../lib/errors';
import { loaiChungTuRepository } from '../../../lib/repositories/ocr-schema.repository';
import { useToast } from '../../../lib/toast';
import { thuLai } from '../../vai-tro/hooks/useVaiTro';
import type {
  OcrColumnInput,
  OcrColumnPatch,
  OcrFieldInput,
  OcrFieldPatch,
  OcrSchemaCreate,
  OcrSchemaDetail,
  OcrSchemaListItem,
  OcrSchemaPatch,
  OcrSchemaStats,
  OcrTableInput,
  OcrTablePatch,
} from '../types';

export const KHOA_LOAI_CHUNG_TU = {
  goc: ['ocr', 'schemas'] as const,
  tatCa: ['ocr', 'schemas', 'tat-ca'] as const,
  thongKe: ['ocr', 'schemas', 'thong-ke'] as const,
  chiTiet: (id: string) => ['ocr', 'schemas', 'chi-tiet', id] as const,
  theoMa: (code: string) => ['ocr', 'schemas', 'ma', code] as const,
};

const MOT_TRANG = 100;
const TRANG_TOI_DA = 10;

/**
 * Mọi loại chứng từ của tenant, nối `next_cursor` tới hết rồi lọc ở client: mỗi tenant vài chục
 * loại, và ô chọn ở màn nhận dạng cần cả danh sách.
 */
export function useDanhSachLoaiChungTu() {
  return useQuery<OcrSchemaListItem[], ApiError>({
    queryKey: KHOA_LOAI_CHUNG_TU.tatCa,
    queryFn: async () => {
      const ra: OcrSchemaListItem[] = [];
      let cursor: string | undefined;
      for (let i = 0; i < TRANG_TOI_DA; i += 1) {
        const trang = await loaiChungTuRepository.danhSach({ limit: MOT_TRANG, cursor });
        ra.push(...trang.items);
        cursor = trang.next_cursor ?? undefined;
        if (!cursor) break;
      }
      return ra;
    },
    staleTime: 60_000,
    retry: thuLai,
  });
}

export function useThongKeLoaiChungTu() {
  return useQuery<OcrSchemaStats, ApiError>({
    queryKey: KHOA_LOAI_CHUNG_TU.thongKe,
    queryFn: loaiChungTuRepository.thongKe,
    staleTime: 60_000,
    retry: thuLai,
  });
}

export function useChiTietLoaiChungTu(id: string | undefined) {
  return useQuery<OcrSchemaDetail, ApiError>({
    queryKey: KHOA_LOAI_CHUNG_TU.chiTiet(id ?? ''),
    queryFn: () => loaiChungTuRepository.chiTiet(id!),
    enabled: Boolean(id),
    staleTime: 30_000,
    retry: thuLai,
  });
}

/** Màn nhận dạng mở theo mã trên URL (`/xu-ly/nhan-dang/:schemaCode`). */
export function useLoaiChungTuTheoMa(code: string | undefined) {
  return useQuery<OcrSchemaDetail, ApiError>({
    queryKey: KHOA_LOAI_CHUNG_TU.theoMa(code ?? ''),
    queryFn: () => loaiChungTuRepository.theoMa(code!),
    enabled: Boolean(code),
    staleTime: 30_000,
    retry: thuLai,
  });
}

/** Mọi thay đổi cấu hình làm cũ danh sách, số liệu và chi tiết: tải lại cả nhánh `ocr/schemas`. */
function useLamMoi() {
  const queryClient = useQueryClient();
  return () => void queryClient.invalidateQueries({ queryKey: KHOA_LOAI_CHUNG_TU.goc });
}

/**
 * Các thao tác ghi của cấu hình loại chứng từ. Không tự bật toast lỗi: form gắn lỗi ràng buộc dưới
 * ô bằng `loiTheoTruong`, lỗi khác hiện trong hộp (hộp không đóng). Toast chỉ báo đã xong.
 */
export function useGhiLoaiChungTu() {
  const lamMoi = useLamMoi();
  const toast = useToast();
  const { t } = useT();
  const xong = (khoa: Parameters<typeof t>[0], thamSo?: Record<string, string>) => {
    lamMoi();
    toast.success(t(khoa, thamSo));
  };

  return {
    tao: useMutation<OcrSchemaDetail, ApiError, OcrSchemaCreate>({
      mutationFn: (du) => loaiChungTuRepository.tao(du),
      onSuccess: (kq) => xong('cauHinhOcr.daTao', { ten: kq.name }),
    }),
    sua: useMutation<OcrSchemaDetail, ApiError, { id: string; du: OcrSchemaPatch }>({
      mutationFn: ({ id, du }) => loaiChungTuRepository.sua(id, du),
      onSuccess: () => xong('cauHinhOcr.daLuu'),
    }),
    xoa: useMutation<void, ApiError, { id: string; ten: string }>({
      mutationFn: ({ id }) => loaiChungTuRepository.xoa(id),
      onSuccess: (_kq, { ten }) => xong('cauHinhOcr.daXoa', { ten }),
    }),
    themTruong: useMutation<unknown, ApiError, { id: string; du: OcrFieldInput }>({
      mutationFn: ({ id, du }) => loaiChungTuRepository.themTruong(id, du),
      onSuccess: () => xong('cauHinhOcr.daThemTruong'),
    }),
    suaTruong: useMutation<unknown, ApiError, { id: string; fieldId: string; du: OcrFieldPatch }>({
      mutationFn: ({ id, fieldId, du }) => loaiChungTuRepository.suaTruong(id, fieldId, du),
      onSuccess: () => xong('cauHinhOcr.daLuu'),
    }),
    xoaTruong: useMutation<void, ApiError, { id: string; fieldId: string }>({
      mutationFn: ({ id, fieldId }) => loaiChungTuRepository.xoaTruong(id, fieldId),
      onSuccess: () => xong('cauHinhOcr.daXoaTruong'),
    }),
    themBang: useMutation<unknown, ApiError, { id: string; du: OcrTableInput }>({
      mutationFn: ({ id, du }) => loaiChungTuRepository.themBang(id, du),
      onSuccess: () => xong('cauHinhOcr.daThemBang'),
    }),
    suaBang: useMutation<unknown, ApiError, { id: string; tableId: string; du: OcrTablePatch }>({
      mutationFn: ({ id, tableId, du }) => loaiChungTuRepository.suaBang(id, tableId, du),
      onSuccess: () => xong('cauHinhOcr.daLuu'),
    }),
    xoaBang: useMutation<void, ApiError, { id: string; tableId: string }>({
      mutationFn: ({ id, tableId }) => loaiChungTuRepository.xoaBang(id, tableId),
      onSuccess: () => xong('cauHinhOcr.daXoaBang'),
    }),
    themCot: useMutation<unknown, ApiError, { id: string; tableId: string; du: OcrColumnInput }>({
      mutationFn: ({ id, tableId, du }) => loaiChungTuRepository.themCot(id, tableId, du),
      onSuccess: () => xong('cauHinhOcr.daThemCot'),
    }),
    suaCot: useMutation<unknown, ApiError, { id: string; tableId: string; columnId: string; du: OcrColumnPatch }>({
      mutationFn: ({ id, tableId, columnId, du }) => loaiChungTuRepository.suaCot(id, tableId, columnId, du),
      onSuccess: () => xong('cauHinhOcr.daLuu'),
    }),
    xoaCot: useMutation<void, ApiError, { id: string; tableId: string; columnId: string }>({
      mutationFn: ({ id, tableId, columnId }) => loaiChungTuRepository.xoaCot(id, tableId, columnId),
      onSuccess: () => xong('cauHinhOcr.daXoaCot'),
    }),
  };
}
